import { useEffect, useState, useCallback } from "react";
import http from "../../services/http";

type State = {
	permissions: string[] | null;
	roleId: string | null;
	roleName: string | null;
	loaded: boolean;
	_allPermissions: boolean;
};

let cached: State = { permissions: null, roleId: null, roleName: null, loaded: false, _allPermissions: false };
let inflight: Promise<void> | null = null;
const listeners = new Set<(s: State) => void>();

function setState(next: Partial<State>) {
	cached = { ...cached, ...next };
	listeners.forEach((fn) => fn(cached));
}

function fetchPermissions(): Promise<void> {
	if (inflight) return inflight;
	inflight = http
		.get("/auth/me", { params: { _t: Date.now() } })
		.then((resp) => {
			const data = resp?.data || {};
			setState({
				permissions: Array.isArray(data.permissions) ? data.permissions : null,
				roleId: data.roleId || null,
				roleName: data.roleName || null,
				loaded: true,
				_allPermissions: data._allPermissions === true,
			});
		})
		.catch(() => {
			setState({ loaded: true, permissions: null, roleId: null, roleName: null, _allPermissions: false });
		})
		.finally(() => {
			inflight = null;
		});
	return inflight;
}

/**
 * Admin web RBAC permission hook.
 * Loads permissions from /auth/me once, caches globally, exposes hasPermission(key).
 *
 * Strict mode: `permissions === null` means no RBAC role → hasPermission returns false.
 * `_allPermissions: true` (CEO + Super Admin) bypasses all permission checks.
 */
export function useAdminPermissions() {
	const [state, setLocalState] = useState<State>(cached);

	useEffect(() => {
		listeners.add(setLocalState);
		if (!cached.loaded) {
			fetchPermissions();
		}
		return () => {
			listeners.delete(setLocalState);
		};
	}, []);

	const hasPermission = useCallback(
		(key: string): boolean => {
			if (!state.loaded) return false;
			if (state._allPermissions) return true;
			if (!Array.isArray(state.permissions)) return false;
			return state.permissions.includes(key);
		},
		[state]
	);

	const hasAny = useCallback(
		(keys: string[]): boolean => {
			if (!state.loaded) return false;
			if (state._allPermissions) return true;
			if (!Array.isArray(state.permissions)) return false;
			return keys.some((k) => state.permissions!.includes(k));
		},
		[state]
	);

	return {
		permissions: state.permissions,
		roleId: state.roleId,
		roleName: state.roleName,
		loaded: state.loaded,
		hasPermission,
		hasAny,
		_allPermissions: state._allPermissions,
	};
}

/** Force-reload permissions (e.g. after role change). */
export function reloadAdminPermissions(): Promise<void> {
	inflight = null;
	setState({ loaded: false });
	return fetchPermissions();
}
